/**
 * "Бичлэг захиалах" form, opened from VideoOrderCard under the demo video in the
 * job-posting popup. Replaced the Gmail compose draft: the order now goes straight to
 * the internal dashboard (sendVideoOrder → internal.diil.work/api/diil-video-order)
 * instead of depending on the employer finishing and sending an email.
 *
 * Same look as PitchRequestModal. Portaled to document.body and stacked above
 * PostJobModal (z-[120]), which it opens on top of.
 */

import { useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { FiX } from "react-icons/fi";
import { sendVideoOrder } from "../api/services/feedback";
import useBodyScrollLock from "../hooks/useBodyScrollLock";
import useEscapeKey from "../hooks/useEscapeKey";
import useDialogFocus from "../hooks/useDialogFocus";

const FIELD_CLASS =
  "w-full rounded-[12px] border border-[#E2E8F0] px-4 py-3 text-[14px] text-[#020618] outline-none focus:border-[#020618] placeholder:text-[#94A3B8]";

const LABEL_CLASS = "block text-[13px] font-medium text-[#020618] mb-1.5";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY = { hrName: "", activity: "", email: "", phone: "", position: "", about: "" };

export default function VideoOrderModal({ isOpen, onClose, companyName = "", phone = "" }) {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  // Phone from the wizard, until they type their own.
  const values = { ...form, phone: form.phone || (form.phoneTouched ? "" : phone) };
  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value, ...(key === "phone" ? { phoneTouched: true } : {}) }));

  const close = () => {
    if (status === "sending") return;
    setForm(EMPTY);
    setStatus("idle");
    onClose?.();
  };

  useBodyScrollLock(isOpen);
  useEscapeKey(isOpen, close);
  const panelRef = useDialogFocus(isOpen);

  const emailOk = EMAIL_RE.test(values.email.trim());
  const canSubmit =
    values.hrName.trim() &&
    values.activity.trim() &&
    emailOk &&
    values.phone.trim() &&
    values.position.trim() &&
    values.about.trim() &&
    status !== "sending";

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setStatus("sending");
    const res = await sendVideoOrder({
      company: companyName,
      hrName: values.hrName,
      activity: values.activity,
      email: values.email,
      phone: values.phone,
      position: values.position,
      about: values.about,
    });
    if (!res.ok) {
      console.error("[VideoOrderModal] order submission failed:", res.error);
      setStatus("error");
      return;
    }
    setStatus("sent");
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="video-order-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[130] flex items-end md:items-center justify-center p-0 md:p-4"
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={close} />

          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="video-order-title"
            tabIndex={-1}
            initial={{ y: 40, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.98, transition: { duration: 0.18 } }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            className="relative w-full md:w-[480px] max-h-[90vh] bg-white rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden flex flex-col focus:outline-none"
          >
            <button
              type="button"
              onClick={close}
              aria-label="Хаах"
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
            >
              <FiX size={18} className="text-slate-600" />
            </button>

            <div className="flex-1 overflow-y-auto px-6 pt-10 pb-[calc(1.75rem+env(safe-area-inset-bottom,0px))] md:px-9 md:pt-11 md:pb-9">
              {status === "sent" ? (
                <div className="text-center px-2 py-2">
                  <p className="text-[44px] leading-none">🎬</p>
                  <p id="video-order-title" className="mt-4 text-[15px] font-medium text-[#020618] leading-relaxed">
                    Захиалга хүлээн авлаа. Бид тантай удахгүй холбогдоно.
                  </p>
                  <button
                    type="button"
                    onClick={close}
                    className="mt-7 w-full h-12 rounded-[99px] text-[15px] font-bold bg-[#020618] text-white hover:bg-[#29EAFF] hover:text-[#0F172B] transition-colors"
                  >
                    Хаах
                  </button>
                </div>
              ) : (
                <>
                  <h2 id="video-order-title" className="text-[22px] md:text-[24px] font-bold text-center text-[#020618] leading-snug px-4">
                    Бичлэг захиалах
                  </h2>
                  <p className="mt-2.5 text-center text-sm text-slate-400 px-2">
                    Мэдээллээ үлдээгээрэй, бид тантай холбогдож бичлэгийн ажлыг тохиролцоно.
                  </p>

                  <div className="mt-7 space-y-3">
                    <div>
                      <label htmlFor="vo-hr-name" className={LABEL_CLASS}>HR-ын нэр</label>
                      <input
                        id="vo-hr-name"
                        className={FIELD_CLASS}
                        placeholder="Холбогдох хүний нэр"
                        value={values.hrName}
                        onChange={set("hrName")}
                      />
                    </div>
                    <div>
                      <label htmlFor="vo-activity" className={LABEL_CLASS}>Үйл ажиллагаа</label>
                      <input
                        id="vo-activity"
                        className={FIELD_CLASS}
                        placeholder="Жишээ: Ресторан, барилга, худалдаа"
                        value={values.activity}
                        onChange={set("activity")}
                      />
                    </div>
                    <div>
                      <label htmlFor="vo-email" className={LABEL_CLASS}>И-мэйл</label>
                      <input
                        id="vo-email"
                        className={FIELD_CLASS}
                        placeholder="И-мэйл"
                        type="email"
                        value={values.email}
                        onChange={set("email")}
                      />
                    </div>
                    <div>
                      <label htmlFor="vo-phone" className={LABEL_CLASS}>Утас</label>
                      <input
                        id="vo-phone"
                        className={FIELD_CLASS}
                        placeholder="Утас"
                        type="tel"
                        inputMode="tel"
                        value={values.phone}
                        onChange={set("phone")}
                      />
                    </div>
                    <div>
                      <label htmlFor="vo-position" className={LABEL_CLASS}>Ямар ажлын байр зарлах</label>
                      <input
                        id="vo-position"
                        className={FIELD_CLASS}
                        placeholder="Жишээ: Тогооч, борлуулагч"
                        value={values.position}
                        onChange={set("position")}
                      />
                    </div>
                    <div>
                      <label htmlFor="vo-about" className={LABEL_CLASS}>Компанийн тухай дэлгэрэнгүй танилцуулга</label>
                      <textarea
                        id="vo-about"
                        rows={4}
                        className={`${FIELD_CLASS} resize-none`}
                        placeholder="Компанийн түүх, хамт олон, давуу тал…"
                        value={values.about}
                        onChange={set("about")}
                      />
                    </div>
                  </div>

                  {status === "error" && (
                    <p className="mt-3 text-[12.5px] font-bold text-[#EF4444] leading-relaxed">
                      Илгээхэд алдаа гарлаа. Дахин оролдоно уу.
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    className={`mt-6 w-full h-12 rounded-[99px] text-[15px] font-bold transition-colors ${
                      canSubmit
                        ? "bg-[#020618] text-white hover:bg-[#29EAFF] hover:text-[#0F172B]"
                        : "bg-[#E2E8F0] text-[#94A3B8]"
                    }`}
                  >
                    {status === "sending" ? "Илгээж байна…" : "Захиалга илгээх"}
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
